import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "wouter";
import { api, ApiError } from "../api";
import { useCan } from "../auth/session";
import { Card, EmptyState, Field, InlineAlert, PageHeader, PageShell, StatusChip, icons } from "../company-ui";
import { Tip } from "../help/Tip";
import { useCompanyScope } from "../shell/company-scope";
import "./wip.css";

/**
 * WIP and inventory (ALN-38, docs/pages/wip.md), laid out as the mockup's wip view: the posted-value
 * callout, Inventory at cost by pool, the Lots table and Packaging and dry goods. Wine figures are
 * posted cost and read-only; the open month's estimate is in the callout only, never in a total.
 * Packaging is entered by hand (no QuickBooks) or overridden (QuickBooks) by owners and controllers.
 */
type Pool = "bulk_tank" | "bulk_barrel" | "bottled_aging" | "finished_goods" | "must_juice" | "grapes" | "crop_in_progress" | "packaging";
interface Lot { id: string; code: string; programId: string | null; wine: string; pool: Pool; vessel: string; gallons: number; cases: number; costCents: number; unitCostCents: number | null; unit: "gal" | "case"; monthsHeld: number }
interface Wip {
  postedThrough: string | null; openPeriod: string; openPeriodPosted: boolean; estimateCents: number; basis: "consolidated";
  intercompanyProfitCents: number | null; totalCents: number; pools: { pool: Pool; cents: number; lots: number; sharePct: number }[]; lots: Lot[];
}
type Kind = "glass" | "closure" | "capsule" | "label" | "carton" | "other";
interface Item {
  id: string; itemId: string; companyId: string; kind: Kind; name: string; source: "quickbooks" | "manual"; qtyOnHand: number; unitCostCents: number; valueCents: number;
  quickbooks: { qty: number; unitCostCents: number } | null; override: { reason: string; by: string | null; at: string } | null; version: number;
  asOf: string; enteredBy: string | null; enteredAt: string | null;
}
interface Packaging { asOf: string; connected: boolean; handCompanies: { id: string; name: string }[]; items: Item[]; totalCents: number }

const SUBTITLE = "What the wine in the building has cost so far, by inventory pool and by lot. Values come from each program's actuals and change with every close and movement.";
const POOL: Record<Pool, string> = { bulk_tank: "Bulk in tank", bulk_barrel: "Bulk in barrel", bottled_aging: "Bottled, aging", finished_goods: "Finished goods", must_juice: "Must and juice", grapes: "Grapes", crop_in_progress: "Crop in progress", packaging: "Packaging" };
const KINDS: { value: Kind; label: string }[] = [
  { value: "glass", label: "Glass" }, { value: "closure", label: "Closure" }, { value: "capsule", label: "Capsule" },
  { value: "label", label: "Label" }, { value: "carton", label: "Carton" }, { value: "other", label: "Other" },
];
const KIND_LABEL = Object.fromEntries(KINDS.map(k => [k.value, k.label])) as Record<Kind, string>;
const COMPLIANCE = "/compliance?tab=borrowing";

const usd = (cents: number, digits = 0) => (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: digits, maximumFractionDigits: digits });
const unitUsd = (cents: number) => (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 4 });
const num = (n: number, max = 2) => n.toLocaleString("en-US", { maximumFractionDigits: max });
const month = (ym: string) => new Date(`${ym.slice(0, 7)}-15T12:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const shortDay = (iso: string) => new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: iso.length === 10 ? "UTC" : undefined });
const plural = (n: number, one: string, many: string) => `${num(n, 0)} ${n === 1 ? one : many}`;
/** A bar segment sized by its pool's cents (a computed style, not a static one). */
const grow = (cents: number) => ({ flexGrow: cents });
const requestId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export function WipPage() {
  const canSee = useCan("costs.view");
  const canEdit = useCan("close.run");
  const { companyId, companies } = useCompanyScope();
  const q = companyId ? `?companyId=${companyId}` : "";
  const wip = useQuery({ queryKey: ["wip", companyId], queryFn: () => api<Wip>(`/inventory/wip${q}`), enabled: canSee, retry: false });
  const pack = useQuery({ queryKey: ["packaging", companyId], queryFn: () => api<Packaging>(`/inventory/packaging${q}`), enabled: canSee, retry: false });

  if (!canSee) {
    return (
      <PageShell>
        <PageHeader title="WIP and inventory" subtitle={SUBTITLE} />
        <InlineAlert variant="info" title="You don't have access to costs and inventory. Ask an owner or controller." />
      </PageShell>
    );
  }
  const report = <Link className="btn" href={COMPLIANCE}>Bank inventory report</Link>;
  const companyName = companyId ? companies.find(c => c.id === companyId)?.name ?? null : null;

  if (wip.isError || pack.isError) {
    return (
      <PageShell>
        <PageHeader title="WIP and inventory" subtitle={SUBTITLE} actions={report} />
        <Card>
          <EmptyState icon={icons.warning} title="WIP and inventory couldn't load. Try again in a minute."
            action={<button type="button" className="btn" onClick={() => { void wip.refetch(); void pack.refetch(); }}>Try again</button>} />
        </Card>
      </PageShell>
    );
  }
  return (
    <PageShell>
      <PageHeader title="WIP and inventory" subtitle={SUBTITLE} actions={report} />
      <div className="wipstack">
        <PostedCallout wip={wip.data ?? null} />
        {wip.data ? <InventoryCard wip={wip.data} companyName={companyName} /> : <Card><div className="wipskel" aria-hidden="true"><i /><i /><i /></div></Card>}
        {wip.data ? <LotsCard lots={wip.data.lots} /> : <Card title="Lots"><div className="wipskel" aria-hidden="true"><i /><i /><i /></div></Card>}
        {pack.data ? <PackagingCard data={pack.data} canEdit={canEdit} companyId={companyId} />
          : <Card title="Packaging and dry goods"><div className="wipskel" aria-hidden="true"><i /><i /></div></Card>}
      </div>
    </PageShell>
  );
}

/* ---------------------------------------------------------------- callout */

function PostedCallout({ wip }: { wip: Wip | null }) {
  const compliance = <>Bank and lender schedules are on <Link href={COMPLIANCE}>Compliance</Link>.</>;
  let text: ReactNode;
  if (!wip) text = "Checking what has posted…";
  else if (!wip.postedThrough) text = <>No month has posted yet. {compliance}</>;
  else if (wip.openPeriodPosted) text = <>Values are posted cost through {month(wip.postedThrough)}. {compliance}</>;
  else text = <>Values are posted cost through {month(wip.postedThrough)}. {month(wip.openPeriod)} isn't booked yet: at plan rates, wine outside a program would add about {usd(wip.estimateCents)}; the close replaces that estimate with actual cost. {compliance}</>;
  return <div className="wipcallout" role="status"><span className="wipcallout__i" aria-hidden="true">{icons.info}</span><p>{text}</p></div>;
}

/* ---------------------------------------------------------------- inventory at cost */

function InventoryCard({ wip, companyName }: { wip: Wip; companyName: string | null }) {
  const lots = wip.pools.reduce((s, p) => s + p.lots, 0);
  return (
    <Card className="wipinv" headingId="wip-inv-h" title={<>Inventory at cost: {usd(wip.totalCents)} <Tip term="Inventory at cost" /></>}
      description={`${plural(lots, "lot", "lots")} · ${companyName ?? "all companies"}`}>
      <div className="wipinv__b">
        {wip.pools.length === 0 ? <p className="muted">No wine on hand yet. Lots appear here when opening balances are loaded or fruit is received.</p> : (
          <>
            <div className="wipbar" role="img" aria-label="Inventory value split by pool">
              {wip.pools.map(p => <span key={p.pool} className={`wipbar__s wippool--${p.pool}`} style={grow(p.cents)} />)}
            </div>
            <div className="wippools">
              {wip.pools.map(p => (
                <div key={p.pool} className="wippool" role="group" aria-label={POOL[p.pool]}>
                  <span className="wippool__k"><i className={`wippool__dot wippool--${p.pool}`} aria-hidden="true" />{POOL[p.pool]}</span>
                  <span className="wippool__v">{usd(p.cents)}</span>
                  <span className="wippool__s">{plural(p.lots, "lot", "lots")} · {num(p.sharePct, 1)}%</span>
                </div>
              ))}
            </div>
          </>
        )}
        {companyName && (
          <div className="wipbasis">
            <p>Lot costs are on the consolidated (farm cost) basis.</p>
            {wip.intercompanyProfitCents !== null && wip.intercompanyProfitCents !== 0 && (
              <div className="wipbasis__ic">
                <p>Intercompany profit in inventory: {usd(wip.intercompanyProfitCents)}. Add it to match {companyName}'s QuickBooks inventory.</p>
                <Tip term="Intercompany profit in inventory" />
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- lots */

function LotsCard({ lots }: { lots: Lot[] }) {
  if (!lots.length) return null;
  return (
    <Card title="Lots" description="Cost follows the gallons through every blend and bottling.">
      <div className="tablewrap wiptable" role="region" aria-label="Lots" tabIndex={0}>
        <table className="t">
          <thead>
            <tr>
              <th scope="col" className="wiplot">Lot</th>
              <th scope="col">Program</th>
              <th scope="col">Pool</th>
              <th scope="col">Vessel</th>
              <th scope="col" className="r">Quantity</th>
              <th scope="col" className="r">Cost to date</th>
              <th scope="col" className="r"><span className="wipth">Unit cost <Tip term="Unit cost" /></span></th>
              <th scope="col" className="r"><span className="wipth">Months held <Tip term="Months held" /></span></th>
            </tr>
          </thead>
          <tbody>
            {lots.map(l => (
              <tr key={l.id}>
                <th scope="row" className="wiplot wipcode">{l.code}</th>
                <td>{l.programId ? <Link href={`/builder?program=${l.programId}`}>{l.wine}</Link> : l.wine}</td>
                <td>{POOL[l.pool]}</td>
                <td>{l.vessel}</td>
                <td className="r">{l.unit === "case" ? plural(l.cases, "case", "cases") : `${num(l.gallons)} gal`}</td>
                <td className="r">{usd(l.costCents)}</td>
                <td className="r">{l.unitCostCents === null ? "" : `${usd(l.unitCostCents, 2)} / ${l.unit}`}</td>
                <td className="r">{l.monthsHeld}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- packaging */

type Panel = { mode: "add" } | { mode: "edit" | "override"; id: string } | null;

function PackagingCard({ data, canEdit, companyId }: { data: Packaging; canEdit: boolean; companyId: string | null }) {
  const [panel, setPanel] = useState<Panel>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const addBtn = useRef<HTMLButtonElement>(null);
  const canAdd = canEdit && data.handCompanies.length > 0;
  const open = (p: Panel) => { trigger.current = document.activeElement as HTMLElement | null; setNotice(null); setPanel(p); };
  const close = () => {
    setPanel(null); setNotice(null);
    requestAnimationFrame(() => {
      const t = trigger.current;
      if (t && t.isConnected) t.focus(); else addBtn.current?.focus();
    });
  };
  const item = panel && panel.mode !== "add" ? data.items.find(i => i.id === panel.id) ?? null : null;

  return (
    <Card className="wippack" headingId="wip-pack-h" title={<>Packaging and dry goods: {usd(data.totalCents)} <Tip term="Packaging and dry goods" /></>}
      description="Raw materials, not yet in a lot. They move into a lot's cost when bottling posts."
      actions={canAdd && data.items.length > 0 ? <button ref={addBtn} type="button" className="btn btn--sm" aria-expanded={panel?.mode === "add"} onClick={() => open({ mode: "add" })}>{icons.plus}Add item</button> : undefined}>
      <div className="wippack__b">
        {data.items.length > 0 && data.handCompanies.map(c => (
          <InlineAlert key={c.id} variant="info" title={`QuickBooks isn't connected for ${c.name}, so packaging is entered by hand.`} />
        ))}
        {panel?.mode === "add" && <AddForm data={data} companyId={companyId} onClose={close} onSaved={close} />}
        {panel && panel.mode === "edit" && item && <EditForm key={`${item.id}:${item.version}`} item={item} period={data.asOf} notice={notice} onNotice={setNotice} onClose={close} />}
        {panel && panel.mode === "override" && item && <OverrideForm key={`${item.id}:${item.version}`} item={item} period={data.asOf} notice={notice} onNotice={setNotice} onClose={close} />}
        {data.items.length === 0 ? (
          data.connected
            ? <EmptyState title="No packaging on hand yet. It appears when dry goods sync from QuickBooks." />
            : <EmptyState title="No packaging on hand yet. QuickBooks isn't connected, so add it by hand."
                action={canAdd && panel?.mode !== "add" ? <button ref={addBtn} type="button" className="btn" onClick={() => open({ mode: "add" })}>{icons.plus}Add item</button> : undefined} />
        ) : (
          <div className="tablewrap wiptable wiptable--pack" role="region" aria-label="Packaging items" tabIndex={0}>
            <table className="t">
              <thead>
                <tr>
                  <th scope="col" className="wiplot">Item</th>
                  <th scope="col" className="r">On hand</th>
                  <th scope="col" className="r">Unit cost</th>
                  <th scope="col" className="r">Value</th>
                  <th scope="col">Source</th>
                  {canEdit && <th scope="col"><span className="sr">Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {data.items.map(i => (
                  <tr key={i.id}>
                    <th scope="row" className="wiplot wipitem"><span className="wipitem__n">{i.name}</span><span className="wipitem__k">{KIND_LABEL[i.kind]}</span></th>
                    <td className="r">{num(i.qtyOnHand)}</td>
                    <td className="r">{unitUsd(i.unitCostCents)}</td>
                    <td className="r">{usd(i.valueCents)}</td>
                    <td><Source item={i} /></td>
                    {canEdit && (
                      <td className="r">
                        {i.source === "quickbooks"
                          ? <button type="button" className="btn btn--ghost btn--sm" onClick={() => open({ mode: "override", id: i.id })}>Override<span className="sr"> {i.name}</span></button>
                          : <button type="button" className="btn btn--ghost btn--sm" onClick={() => open({ mode: "edit", id: i.id })}>Edit<span className="sr"> {i.name}</span></button>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

function Source({ item }: { item: Item }) {
  if (item.source === "manual") return <>Entered by {item.enteredBy ?? "hand"}, {shortDay(item.enteredAt ?? item.asOf)}</>;
  if (!item.override) return <>QuickBooks, {shortDay(item.asOf)}</>;
  const qb = item.quickbooks!;
  const sameCost = qb.unitCostCents === item.unitCostCents;
  return (
    <span className="wipsrc" title={`Reason: ${item.override.reason}`}>
      <StatusChip tone="warning">Overridden</StatusChip>
      <Tip term="Overridden" />
      <span className="wipsrc__qb">QuickBooks: {num(qb.qty)}{sameCost ? "" : ` at ${unitUsd(qb.unitCostCents)}`}</span>
      <span className="sr">Reason: {item.override.reason}</span>
    </span>
  );
}

/* ---------------------------------------------------------------- forms */

const NUMBER_ERROR = "Enter a number of zero or more.";
const parseAmount = (v: string): number | null => {
  const t = v.replace(/[$,\s]/g, "");
  if (!t || !/^\d*\.?\d*$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
};
const toCents = (dollars: number) => Number((dollars * 100).toFixed(4));
const dollarsOf = (cents: number) => String(Number((cents / 100).toFixed(6)));

/** Escape closes a panel; the parent returns focus to what opened it. */
function usePanel(onClose: () => void) {
  const first = useRef<HTMLSelectElement & HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const onKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
  return { first, onKeyDown };
}
/** Server refusals land on their field when they name one, else at the top of the panel. */
function fieldOf(e: unknown): { field: string; message: string } {
  const message = e instanceof ApiError ? e.message : "Something went wrong. Check your connection and try again.";
  if (!(e instanceof ApiError)) return { field: "form", message };
  if (e.code === "name_taken") return { field: "name", message };
  if (e.code === "quickbooks_connected") return { field: "companyId", message };
  if (e.message === "Give a reason for the override.") return { field: "reason", message };
  if (e.message === NUMBER_ERROR) return { field: "qtyOnHand", message };
  if (e.message === "Name the item.") return { field: "name", message };
  if (e.message === "Pick the kind of packaging.") return { field: "kind", message };
  if (e.message === "Pick the company this item belongs to.") return { field: "companyId", message };
  return { field: "form", message };
}
function useSave() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const run = async (path: string, method: string, body: object) => {
    setBusy(true);
    try { return await api<{ item?: Item }>(path, { method, body, headers: { "X-Request-Id": requestId() } }); }
    finally { setBusy(false); void qc.invalidateQueries({ queryKey: ["packaging"] }); }
  };
  return { busy, run };
}

function AddForm({ data, companyId, onClose, onSaved }: { data: Packaging; companyId: string | null; onClose: () => void; onSaved: () => void }) {
  const askCompany = companyId === null;
  const [company, setCompany] = useState(askCompany ? (data.handCompanies.length === 1 ? data.handCompanies[0]!.id : "") : companyId);
  const [kind, setKind] = useState("");
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [cost, setCost] = useState("");
  const [err, setErr] = useState<{ field: string; message: string } | null>(null);
  const { busy, run } = useSave();
  const { first, onKeyDown } = usePanel(onClose);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const q = parseAmount(qty), c = parseAmount(cost);
    const problem = !company ? { field: "companyId", message: "Pick the company this item belongs to." }
      : !kind ? { field: "kind", message: "Pick the kind of packaging." }
      : !name.trim() ? { field: "name", message: "Name the item." }
      : q === null ? { field: "qtyOnHand", message: NUMBER_ERROR }
      : c === null ? { field: "unitCostCents", message: NUMBER_ERROR } : null;
    setErr(problem);
    if (problem) return;
    try {
      await run("/inventory/packaging", "POST", { companyId: company, kind, name: name.trim(), qtyOnHand: q, unitCostCents: toCents(c!), period: data.asOf });
      onSaved();
    } catch (x) { setErr(fieldOf(x)); }
  };
  const errOf = (f: string) => (err?.field === f ? err.message : null);
  return (
    <form className="wippanel" aria-label="Add packaging" onSubmit={submit} onKeyDown={onKeyDown} noValidate>
      <h3 className="wippanel__t">Add packaging</h3>
      <p className="wippanel__d">As of {month(data.asOf)}, the open month.</p>
      {err?.field === "form" && <InlineAlert variant="error" title={err.message} />}
      <div className="wippanel__grid">
        {askCompany && (
          <Field label="Company" hint="Only companies not connected to QuickBooks. Connected companies' packaging comes from QuickBooks." error={errOf("companyId")}>
            <select ref={first} value={company ?? ""} onChange={e => setCompany(e.target.value)}>
              <option value="">Pick a company</option>
              {data.handCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Kind" error={errOf("kind")}>
          <select ref={askCompany ? undefined : first} value={kind} onChange={e => setKind(e.target.value)}>
            <option value="">Pick a kind</option>
            {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </Field>
        <Field label="Name" error={errOf("name")}><input className="input" value={name} maxLength={200} onChange={e => setName(e.target.value)} /></Field>
        <Field label="Quantity on hand" error={errOf("qtyOnHand")}><input className="input num" inputMode="decimal" value={qty} onChange={e => setQty(e.target.value)} /></Field>
        <Field label="Unit cost" error={errOf("unitCostCents")}><input className="input num" inputMode="decimal" value={cost} onChange={e => setCost(e.target.value)} /></Field>
      </div>
      <div className="wippanel__a">
        <button type="submit" className="btn btn--primary" disabled={busy}>Save</button>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}

interface RowFormProps { item: Item; period: string; notice: string | null; onNotice: (m: string | null) => void; onClose: () => void }

function EditForm({ item, period, notice, onNotice, onClose }: RowFormProps) {
  const [kind, setKind] = useState<string>(item.kind);
  const [name, setName] = useState(item.name);
  const [qty, setQty] = useState(String(item.qtyOnHand));
  const [cost, setCost] = useState(dollarsOf(item.unitCostCents));
  const [err, setErr] = useState<{ field: string; message: string } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const { busy, run } = useSave();
  const { first, onKeyDown } = usePanel(onClose);
  const conflict = (x: unknown) => {
    const f = fieldOf(x);
    if (x instanceof ApiError && x.code === "edit_conflict") onNotice(f.message); else setErr(f);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const q = parseAmount(qty), c = parseAmount(cost);
    const problem = !kind ? { field: "kind", message: "Pick the kind of packaging." }
      : !name.trim() ? { field: "name", message: "Name the item." }
      : q === null ? { field: "qtyOnHand", message: NUMBER_ERROR }
      : c === null ? { field: "unitCostCents", message: NUMBER_ERROR } : null;
    setErr(problem);
    if (problem) return;
    try {
      await run(`/inventory/packaging/${item.id}`, "PATCH", { kind, name: name.trim(), qtyOnHand: q, unitCostCents: toCents(c!), version: item.version, period });
      onClose();
    } catch (x) { conflict(x); }
  };
  const remove = async () => {
    try { await run(`/inventory/packaging/${item.id}`, "DELETE", { version: item.version, period }); onClose(); }
    catch (x) { setConfirming(false); conflict(x); }
  };
  const errOf = (f: string) => (err?.field === f ? err.message : null);
  return (
    <form className="wippanel" aria-label={`Edit ${item.name}`} onSubmit={submit} onKeyDown={onKeyDown} noValidate>
      <h3 className="wippanel__t">Edit {item.name}</h3>
      <p className="wippanel__d">As of {month(period)}, the open month.</p>
      {notice && <InlineAlert variant="warning" title={notice} />}
      {err?.field === "form" && <InlineAlert variant="error" title={err.message} />}
      <div className="wippanel__grid">
        <Field label="Kind" error={errOf("kind")}>
          <select ref={first} value={kind} onChange={e => setKind(e.target.value)}>
            {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </Field>
        <Field label="Name" error={errOf("name")}><input className="input" value={name} maxLength={200} onChange={e => setName(e.target.value)} /></Field>
        <Field label="Quantity on hand" error={errOf("qtyOnHand")}><input className="input num" inputMode="decimal" value={qty} onChange={e => setQty(e.target.value)} /></Field>
        <Field label="Unit cost" error={errOf("unitCostCents")}><input className="input num" inputMode="decimal" value={cost} onChange={e => setCost(e.target.value)} /></Field>
      </div>
      {confirming ? (
        <div className="wipconfirm" role="alertdialog" aria-label={`Remove ${item.name}`}>
          <p>Remove {item.name}? It's no longer on hand from this month.</p>
          <div className="wippanel__a">
            <button type="button" className="btn btn--destructive" autoFocus disabled={busy} onClick={() => void remove()}>Remove</button>
            <button type="button" className="btn" onClick={() => setConfirming(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="wippanel__a">
          <button type="submit" className="btn btn--primary" disabled={busy}>Save</button>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn--ghost wippanel__end" onClick={() => setConfirming(true)}>Remove</button>
        </div>
      )}
    </form>
  );
}

function OverrideForm({ item, period, notice, onNotice, onClose }: RowFormProps) {
  const qb = item.quickbooks!;
  const [qty, setQty] = useState(String(item.qtyOnHand));
  const [cost, setCost] = useState(dollarsOf(item.unitCostCents));
  const [reason, setReason] = useState(item.override?.reason ?? "");
  const [err, setErr] = useState<{ field: string; message: string } | null>(null);
  const { busy, run } = useSave();
  const { first, onKeyDown } = usePanel(onClose);
  const fail = (x: unknown) => {
    const f = fieldOf(x);
    if (x instanceof ApiError && x.code === "edit_conflict") onNotice(f.message); else setErr(f);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const q = parseAmount(qty), c = parseAmount(cost);
    const problem = q === null ? { field: "qtyOnHand", message: NUMBER_ERROR }
      : c === null ? { field: "unitCostCents", message: NUMBER_ERROR }
      : !reason.trim() ? { field: "reason", message: "Give a reason for the override." } : null;
    setErr(problem);
    if (problem) return;
    try {
      await run(`/inventory/packaging/${item.id}/override`, "PUT", { qtyOnHand: q, unitCostCents: toCents(c!), reason: reason.trim(), version: item.version, period });
      onClose();
    } catch (x) { fail(x); }
  };
  const clear = async () => {
    try { await run(`/inventory/packaging/${item.id}/override`, "DELETE", { version: item.version, period }); onClose(); }
    catch (x) { fail(x); }
  };
  const errOf = (f: string) => (err?.field === f ? err.message : null);
  return (
    <form className="wippanel" aria-label={`Override ${item.name}`} onSubmit={submit} onKeyDown={onKeyDown} noValidate>
      <h3 className="wippanel__t">Override {item.name}</h3>
      <p className="wippanel__d">QuickBooks says {num(qb.qty)} at {unitUsd(qb.unitCostCents)}. Your figures show here until the next sync.</p>
      {notice && <InlineAlert variant="warning" title={notice} />}
      {err?.field === "form" && <InlineAlert variant="error" title={err.message} />}
      <div className="wippanel__grid">
        <Field label="Quantity on hand" error={errOf("qtyOnHand")}><input ref={first} className="input num" inputMode="decimal" value={qty} onChange={e => setQty(e.target.value)} /></Field>
        <Field label="Unit cost" error={errOf("unitCostCents")}><input className="input num" inputMode="decimal" value={cost} onChange={e => setCost(e.target.value)} /></Field>
        <Field label="Reason" className="wippanel__wide" error={errOf("reason")}><input className="input" value={reason} maxLength={200} onChange={e => setReason(e.target.value)} /></Field>
      </div>
      <div className="wippanel__a">
        <button type="submit" className="btn btn--primary" disabled={busy}>Save</button>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        {item.override && <button type="button" className="btn btn--ghost wippanel__end" disabled={busy} onClick={() => void clear()}>Clear override</button>}
      </div>
    </form>
  );
}
